import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type UnsavedChangesDialogContextType = {
  confirmLeave: () => Promise<boolean>;
};

const UnsavedChangesDialogContext = createContext<UnsavedChangesDialogContextType | null>(null);

export function UnsavedChangesDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

  const resolveAndClose = useCallback(
    (value: boolean) => {
      resolver?.(value);
      setResolver(null);
      setOpen(false);
    },
    [resolver]
  );

  const confirmLeave = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve);
      setOpen(true);
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      confirmLeave,
    }),
    [confirmLeave]
  );

  return (
    <UnsavedChangesDialogContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            resolveAndClose(false);
            return;
          }
          setOpen(nextOpen);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription className="mt-4">
              You have unsaved changes. Are you sure you want to leave this page?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => resolveAndClose(false)}>
              Stay
            </Button>
            <Button type="button" variant="destructive" onClick={() => resolveAndClose(true)}>
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UnsavedChangesDialogContext.Provider>
  );
}

export function useUnsavedChangesDialog() {
  const context = useContext(UnsavedChangesDialogContext);

  if (!context) {
    return {
      confirmLeave: async () => window.confirm("You have unsaved changes. Leave this page?"),
    };
  }

  return context;
}
